import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import axios from 'axios';
import config from '../../Config';
import Loading from '../../Components/Loading';
import { logoutUserAtom, authUserAtom, isAuthAtom, dataUserAtom, userRoleAtom, darkModeAtom } from '../../Services/store/atoms';
import { resetTemporaryAtom } from '../../Services/store/atoms';
import DashboardLayout from '../../Layouts/DashboardLayout';
import { useIsInDashboard } from '../../Layouts/DashboardContext';
import { PiBankFill, PiBankLight, PiBellFill, PiBellLight, PiChatCircleFill, PiChatCircleLight, PiDevicesFill, PiDevicesLight, PiIdentificationCardFill, PiIdentificationCardLight, PiLinkFill, PiLinkLight, PiNewspaperClippingFill, PiNewspaperClippingLight, PiNewspaperFill, PiNewspaperLight, PiPackageFill, PiPackageLight, PiPlusBold, PiProhibitFill, PiProhibitLight, PiShareNetworkFill, PiShareNetworkLight, PiSignOutLight, PiStackFill, PiStackLight, PiTrendUpFill, PiTrendUpLight, PiUserCircleFill, PiUserCircleLight, PiUserGearFill, PiUserGearLight, PiUsersFill, PiUsersLight, PiSignInLight } from 'react-icons/pi';
import { navIcon, navIconBadged } from '../../Components/SideBar/navIcon';
import SideBarMenuItem from '../../Components/SideBar/SideBarMenuItem';
import ShareProfileModal from '../../Components/ShareProfileModal';

function LayoutHomeUser(props) {
    const history = useHistory();
    const [isAuth] = useAtom(isAuthAtom);
    const [dataUser] = useAtom(dataUserAtom);
    const [darkMode] = useAtom(darkModeAtom);
    const logoutUser = useSetAtom(logoutUserAtom);
    const resetTemporary = useSetAtom(resetTemporaryAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [canRefer, setCanRefer] = useState(false);
    const [userRole] = useAtom(userRoleAtom);
    const [authUser] = useAtom(authUserAtom);
    const isInDashboard = useIsInDashboard();

    // Muestra el acceso "Mi link de referido" sólo si el usuario tiene permiso (can_refer).
    useEffect(() => {
        let active = true;
        if (!authUser?.token) { setCanRefer(false); return; }
        axios.get(`${config.REACT_APP_BASE_URL}/api/v1/user/referral-link`, {
            headers: { Authorization: `Bearer ${authUser.token}` },
        })
            .then(() => { if (active) setCanRefer(true); })
            .catch(() => { if (active) setCanRefer(false); });
        return () => { active = false; };
    }, [authUser?.token]);

    if (isInDashboard) return <>{props.children}</>;

    const logout = () => {
        logoutUser();
        resetTemporary();
        history.push('/');
    };

    const userId = dataUser?.id;

    const baseRole = userRole || authUser?.role || 'model';

    // Mismo criterio que el guard de RouteCheck: mientras falten estos datos
    // básicos, cualquier ruta privada de modelo redirige a
    // /model/complete-registration, así que ofrecer estos links en el menú
    // solo termina en un rebote — se reemplazan por un aviso no clickeable.
    const isProfileIncomplete = baseRole === 'model' && (
        !dataUser?.name || !dataUser?.nickname || !dataUser?.phone ||
        !dataUser?.birthdate || dataUser?.birthdate === '2000-01-01'
    );

    const links = isProfileIncomplete ? [
        {
            label: 'Modelo',
            items: [
                { title: 'Completa tu perfil', ...navIcon(PiUserCircleLight, PiUserCircleFill), disabled: true },
            ],
        },
    ] : [
        {
            label: 'General',
            items: [
                { title: 'Models', ...navIcon(PiUsersLight, PiUsersFill), url: '/model/models', translate: 'no' },
                { title: 'Packs', ...navIcon(PiPackageLight, PiPackageFill), url: '/model/packs', translate: 'no' },
                { title: 'Feed', ...navIcon(PiNewspaperLight, PiNewspaperFill), url: '/model/feedPublic', translate: 'no' },
            ],
        },
        {
            label: 'Modelo',
            items: [
                { title: 'Mi Muro', ...navIcon(PiNewspaperClippingLight, PiNewspaperClippingFill), url: `/${baseRole}/feed`, translate: 'no' },
                { title: 'Subir Pack', ...navIconBadged(PiPackageLight, PiPackageFill, PiPlusBold), url: `/${baseRole}/uploadpack` },
                { title: 'Lista de Packs', ...navIcon(PiStackLight, PiStackFill), url: `/${baseRole}/listpacks` },
                ...(userId ? [{ title: 'Perfil Público', ...navIcon(PiUserCircleLight, PiUserCircleFill), url: `/${baseRole}/profileModal/${userId}` }] : []),
                { title: 'Compartir Perfil', ...navIcon(PiShareNetworkLight, PiShareNetworkFill), onClick: () => setShowShareModal(true) },
                ...(canRefer ? [{ title: 'Mi Link de Referido', ...navIcon(PiLinkLight, PiLinkFill), url: `/${baseRole}/referral` }] : []),
                { title: 'Chat', ...navIcon(PiChatCircleLight, PiChatCircleFill), url: `/${baseRole}/chat` },
            ],
        },
        {
            label: 'Gestión',
            items: [
                { title: 'Perfil', ...navIcon(PiUserGearLight, PiUserGearFill), url: `/${baseRole}/profile` },
                { title: 'Ventas', ...navIcon(PiTrendUpLight, PiTrendUpFill), url: `/${baseRole}/SalesModel` },
                { title: 'Cuentas Bancarias', ...navIcon(PiBankLight, PiBankFill), url: `/${baseRole}/paymentMethods` },
                { title: 'Documentos', ...navIcon(PiIdentificationCardLight, PiIdentificationCardFill), url: `/${baseRole}/documents` },
                { title: 'Usuarios bloqueados', ...navIcon(PiProhibitLight, PiProhibitFill), url: `/${baseRole}/blocked` },
                { title: 'Sesiones activas', ...navIcon(PiDevicesLight, PiDevicesFill), url: `/${baseRole}/sessions` },
                { title: 'Notificaciones', ...navIcon(PiBellLight, PiBellFill), url: `/${baseRole}/settings/notifications` },
            ],
        },
    ];

    const sidebarFooter = (
        <>
            {isAuth ? (
                <SideBarMenuItem
                    title="Cerrar Sesión"
                    icon={<PiSignOutLight className="text-xl" />}
                    onClick={() => setShowLogoutConfirm(true)}
                    variant="footer"
                    tone="danger"
                />
            ) : (
                <SideBarMenuItem
                    title="Iniciar Sesión"
                    icon={<PiSignInLight className="text-xl" />}
                    url="/login"
                    variant="footer"
                    tone="accent"
                />
            )}
        </>
    );

    return (
        <>
            {isLoading && <Loading />}
            <ShareProfileModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                nickname={dataUser?.nickname}
            />
            <DashboardLayout
                title={props.title}
                links={links}
                showLogo={props.showLogo}
                fullScreen={props.fullScreen}
                sidebarFooter={sidebarFooter}
                logoutConfirmation={{
                    show: showLogoutConfirm,
                    onOpen: () => setShowLogoutConfirm(true),
                    onConfirm: () => { setShowLogoutConfirm(false); logout(); },
                    onCancel: () => setShowLogoutConfirm(false),
                }}
            >
                {props.children}
            </DashboardLayout>
        </>
    );
}

export default LayoutHomeUser;
